import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Loader2, Plus, Pencil, Trash2, Package2, AlertTriangle,
  CheckCircle, Clock, ArrowUpCircle, ArrowDownCircle, RefreshCw,
  TrendingDown, ShoppingCart, Warehouse, List,
} from 'lucide-react';

const UNITS = ['pz', 'kg', 'g', 'l', 'ml', 'm', 'm²', 'm³', 'scatola', 'busta', 'rotolo'];
const MOVEMENT_TYPES = [
  { value: 'purchase',      label: 'Acquisto',    color: 'text-green-600' },
  { value: 'production_out',label: 'Produzione',  color: 'text-red-500' },
  { value: 'adjustment',    label: 'Rettifica',   color: 'text-blue-500' },
  { value: 'return',        label: 'Reso',        color: 'text-amber-600' },
];

const ALERT_CONFIG = {
  red:    { label: 'Sotto scorta',   icon: AlertTriangle, cls: 'text-red-600 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-950/30    border-red-200 dark:border-red-800' },
  yellow: { label: 'Scorta bassa',   icon: Clock,         cls: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' },
  green:  { label: 'Scorta OK',      icon: CheckCircle,   cls: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950/30  border-green-200 dark:border-green-800' },
};

const fmt = (n, dec = 2) => n == null ? '—' : Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: dec });
const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Alert Badge ───────────────────────────────────────────────────────────────
function AlertBadge({ alert, size = 'sm' }) {
  const cfg = ALERT_CONFIG[alert] || ALERT_CONFIG.green;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs font-medium ${cfg.bg} ${cfg.cls}`}>
      <Icon size={10} />
      {size !== 'icon' && cfg.label}
    </span>
  );
}

// ── Material Form Dialog ──────────────────────────────────────────────────────
function MaterialForm({ open, onClose, onSave, initial }) {
  const blank = {
    name: '', sku: '', unit: 'pz', current_stock: '', min_stock: '',
    reorder_qty: '', lead_time_days: 7, supplier: '', unit_cost: '', notes: '',
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    setForm(initial ? { ...blank, ...initial } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Nome obbligatorio'); return; }
    onSave({
      ...form,
      current_stock: parseFloat(form.current_stock) || 0,
      min_stock: parseFloat(form.min_stock) || 0,
      reorder_qty: parseFloat(form.reorder_qty) || 0,
      lead_time_days: parseInt(form.lead_time_days) || 7,
      unit_cost: parseFloat(form.unit_cost) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {initial ? 'Modifica Materiale' : 'Nuovo Materiale'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Nome materiale *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="es. Vasi Plastica Ø9cm" className="h-8" data-testid="mat-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">SKU / Codice</Label>
              <Input value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="MAT-001" className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Unità di misura</Label>
              <Select value={form.unit} onValueChange={v => set('unit', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Giacenza attuale</Label>
              <Input type="number" value={form.current_stock} onChange={e => set('current_stock', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Scorta minima</Label>
              <Input type="number" value={form.min_stock} onChange={e => set('min_stock', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Qtà riordino</Label>
              <Input type="number" value={form.reorder_qty} onChange={e => set('reorder_qty', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Lead time (giorni)</Label>
              <Input type="number" value={form.lead_time_days} onChange={e => set('lead_time_days', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Costo unitario (€)</Label>
              <Input type="number" step="0.01" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} className="h-8" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Fornitore principale</Label>
              <Input value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="Nome fornitore" className="h-8" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Input value={form.notes} onChange={e => set('notes', e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="h-8">Annulla</Button>
            <Button type="submit" className="h-8" data-testid="mat-save-btn">
              {initial ? 'Salva modifiche' : 'Crea materiale'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Movement Form Dialog ──────────────────────────────────────────────────────
function MovementForm({ open, onClose, onSave, materials }) {
  const blank = {
    material_id: '', movement_type: 'purchase',
    quantity: '', unit_cost: '', notes: '',
    movement_date: new Date().toISOString().slice(0, 10),
  };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.material_id) { toast.error('Seleziona il materiale'); return; }
    if (!form.quantity)    { toast.error('Quantità obbligatoria'); return; }
    const mat = materials.find(m => m.id === form.material_id);
    onSave({
      ...form,
      material_name: mat?.name || '',
      quantity: parseFloat(form.quantity) || 0,
      unit_cost: parseFloat(form.unit_cost) || 0,
    });
  };

  const isOut = ['production_out'].includes(form.movement_type);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nuovo Movimento</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Materiale *</Label>
            <Select value={form.material_id} onValueChange={v => {
              const mat = materials.find(m => m.id === v);
              set('material_id', v);
              if (mat) set('unit_cost', mat.unit_cost || '');
            }}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Seleziona materiale" /></SelectTrigger>
              <SelectContent className="max-h-48">
                {materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.unit}) — {fmt(m.current_stock)} disponibili
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo movimento</Label>
              <Select value={form.movement_type} onValueChange={v => set('movement_type', v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Quantità {isOut ? '(scarico)' : '(carico)'}
              </Label>
              <Input type="number" step="0.001" value={form.quantity} onChange={e => set('quantity', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Costo unitario (€)</Label>
              <Input type="number" step="0.01" value={form.unit_cost} onChange={e => set('unit_cost', e.target.value)} className="h-8" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data</Label>
              <Input type="date" value={form.movement_date} onChange={e => set('movement_date', e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note</Label>
            <Input value={form.notes} onChange={e => set('notes', e.target.value)} className="h-8" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-8">Annulla</Button>
            <Button type="submit" className="h-8">Registra</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function InventoryDashboard({ onSeeded }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/dashboard`);
      setData(r.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/inventory/seed-demo`);
      toast.success(r.data.message);
      await load();
      onSeeded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore seed demo');
    } finally { setSeeding(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>;

  const d = data || {};
  const kpis = [
    { label: 'Materiali totali', value: d.total_materials || 0, icon: Package2, color: 'text-blue-600' },
    { label: 'Sotto scorta',     value: d.alert_red || 0,       icon: AlertTriangle, color: 'text-red-600' },
    { label: 'Scorta bassa',     value: d.alert_yellow || 0,    icon: Clock, color: 'text-amber-600' },
    { label: 'Valore magazzino', value: fmtEur(d.total_stock_value), icon: Warehouse, color: 'text-emerald-600', isText: true },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(k => (
          <Card key={k.label}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <k.icon size={16} className={k.color} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{k.label}</p>
                  <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alerts */}
      {(d.critical_materials?.length > 0) ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle size={14} className="text-red-500" />
              Materiali critici — intervento richiesto
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/40 text-muted-foreground">
                    <th className="text-left px-4 py-2">Materiale</th>
                    <th className="text-left px-4 py-2">SKU</th>
                    <th className="text-right px-4 py-2">Giacenza</th>
                    <th className="text-right px-4 py-2">Scorta min</th>
                    <th className="text-right px-4 py-2">Riordino</th>
                    <th className="text-left px-4 py-2">Fornitore</th>
                    <th className="text-right px-4 py-2">Lead time</th>
                    <th className="text-center px-4 py-2">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {d.critical_materials.map(m => (
                    <tr key={m.id} className="border-b hover:bg-muted/20">
                      <td className="px-4 py-2 font-medium">{m.name}</td>
                      <td className="px-4 py-2 text-muted-foreground font-mono">{m.sku || '—'}</td>
                      <td className="px-4 py-2 text-right font-semibold">{fmt(m.current_stock, 4)}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{fmt(m.min_stock, 4)}</td>
                      <td className="px-4 py-2 text-right">{fmt(m.reorder_qty, 4)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{m.supplier || '—'}</td>
                      <td className="px-4 py-2 text-right">{m.lead_time_days || '—'} gg</td>
                      <td className="px-4 py-2 text-center"><AlertBadge alert={m.alert} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle size={32} className="mx-auto mb-3 text-green-500" />
            <p className="text-sm font-medium">Tutte le scorte sono nella norma</p>
            {(!d.total_materials || d.total_materials === 0) && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-3">Nessun materiale presente. Carica i dati demo per iniziare.</p>
                <Button onClick={seedDemo} disabled={seeding} size="sm" variant="outline" className="gap-2">
                  {seeding ? <Loader2 size={12} className="animate-spin" /> : <Package2 size={12} />}
                  Carica dati demo (15 materiali)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent movements */}
      {d.recent_movements?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Ultimi movimenti</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40 text-muted-foreground">
                  <th className="text-left px-4 py-2">Data</th>
                  <th className="text-left px-4 py-2">Materiale</th>
                  <th className="text-left px-4 py-2">Tipo</th>
                  <th className="text-right px-4 py-2">Quantità</th>
                  <th className="text-right px-4 py-2">Costo tot.</th>
                </tr>
              </thead>
              <tbody>
                {d.recent_movements.map(m => {
                  const mt = MOVEMENT_TYPES.find(t => t.value === m.movement_type);
                  return (
                    <tr key={m.id} className="border-b hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono">{m.movement_date}</td>
                      <td className="px-4 py-2 font-medium">{m.material_name}</td>
                      <td className={`px-4 py-2 ${mt?.color || ''}`}>{mt?.label || m.movement_type}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${m.quantity >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {m.quantity >= 0 ? '+' : ''}{fmt(m.quantity, 4)}
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{fmtEur(m.total_cost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function InventoryPage() {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [alertFilter, setAlertFilter] = useState('all');
  const [matForm, setMatForm]     = useState({ open: false, initial: null });
  const [movForm, setMovForm]     = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [seededFlag, setSeededFlag] = useState(0);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (alertFilter !== 'all') params.alert = alertFilter;
      const [mRes, movRes] = await Promise.all([
        axios.get(`${API}/inventory/materials`, { params }),
        axios.get(`${API}/inventory/movements`),
      ]);
      setMaterials(mRes.data);
      setMovements(movRes.data);
    } catch { toast.error('Errore caricamento scorte'); }
    finally { setLoading(false); }
  }, [search, alertFilter, seededFlag]); // eslint-disable-line

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const saveMaterial = async (data) => {
    try {
      if (matForm.initial) {
        await axios.put(`${API}/inventory/materials/${matForm.initial.id}`, data);
        toast.success('Materiale aggiornato');
      } else {
        await axios.post(`${API}/inventory/materials`, data);
        toast.success('Materiale creato');
      }
      setMatForm({ open: false, initial: null });
      loadMaterials();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteMaterial = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/inventory/materials/${id}`);
      toast.success('Eliminato');
      loadMaterials();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
    finally { setDeleting(null); }
  };

  const saveMovement = async (data) => {
    try {
      await axios.post(`${API}/inventory/movements`, data);
      toast.success('Movimento registrato');
      setMovForm(false);
      loadMaterials();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const alertCounts = { all: materials.length };
  materials.forEach(m => { alertCounts[m.alert] = (alertCounts[m.alert] || 0) + 1; });

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestione Scorte
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Materiali, approvvigionamenti e movimenti di magazzino</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadMaterials} className="gap-2">
            <RefreshCw size={13} />
          </Button>
          <Button onClick={() => setMovForm(true)} variant="outline" size="sm" className="gap-2" data-testid="new-movement-btn">
            <ArrowUpCircle size={13} />Movimento
          </Button>
          {isAdmin && (
            <Button onClick={() => setMatForm({ open: true, initial: null })} size="sm" className="gap-2" data-testid="new-material-btn">
              <Plus size={13} />Nuovo materiale
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList data-testid="inventory-tabs">
          <TabsTrigger value="dashboard"><Warehouse size={12} className="mr-1.5" />Dashboard</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials"><Package2 size={12} className="mr-1.5" />Materiali</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements"><List size={12} className="mr-1.5" />Movimenti</TabsTrigger>
        </TabsList>

        {/* ── Dashboard ── */}
        <TabsContent value="dashboard" className="mt-4">
          <InventoryDashboard onSeeded={() => setSeededFlag(f => f + 1)} />
        </TabsContent>

        {/* ── Materials ── */}
        <TabsContent value="materials" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Input placeholder="Cerca materiale, SKU, fornitore..." value={search}
                onChange={e => setSearch(e.target.value)} className="h-8 max-w-xs text-sm" />
              <div className="flex items-center gap-1">
                {[
                  { k: 'all',    label: `Tutti (${alertCounts.all || 0})` },
                  { k: 'red',    label: `🔴 Critici (${alertCounts.red || 0})` },
                  { k: 'yellow', label: `🟡 Bassi (${alertCounts.yellow || 0})` },
                  { k: 'green',  label: `🟢 OK (${alertCounts.green || 0})` },
                ].map(f => (
                  <button key={f.k} onClick={() => setAlertFilter(f.k)}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-all ${alertFilter === f.k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/40 text-muted-foreground">
                          <th className="text-center px-3 py-2 w-8">Stato</th>
                          <th className="text-left px-3 py-2">Materiale</th>
                          <th className="text-left px-3 py-2">SKU</th>
                          <th className="text-left px-3 py-2">UdM</th>
                          <th className="text-right px-3 py-2">Giacenza</th>
                          <th className="text-right px-3 py-2">Disponibile</th>
                          <th className="text-right px-3 py-2">Scorta min</th>
                          <th className="text-right px-3 py-2">Riordino</th>
                          <th className="text-right px-3 py-2">Costo unit.</th>
                          <th className="text-left px-3 py-2">Fornitore</th>
                          <th className="text-right px-3 py-2">Lead time</th>
                          {isAdmin && <th className="w-16"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {materials.length === 0 ? (
                          <tr><td colSpan={12} className="text-center py-10 text-muted-foreground">
                            Nessun materiale trovato — vai su Dashboard per caricare i dati demo
                          </td></tr>
                        ) : materials.map(m => (
                          <tr key={m.id} className="border-b hover:bg-muted/20 group">
                            <td className="px-3 py-2 text-center">
                              <AlertBadge alert={m.alert} size="icon" />
                            </td>
                            <td className="px-3 py-2 font-medium">{m.name}</td>
                            <td className="px-3 py-2 font-mono text-muted-foreground">{m.sku || '—'}</td>
                            <td className="px-3 py-2">{m.unit}</td>
                            <td className={`px-3 py-2 text-right font-semibold ${m.alert === 'red' ? 'text-red-500 dark:text-red-400' : m.alert === 'yellow' ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                              {fmt(m.current_stock, 4)}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{fmt(m.available_qty, 4)}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{fmt(m.min_stock, 4)}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{fmt(m.reorder_qty, 4)}</td>
                            <td className="px-3 py-2 text-right">{fmtEur(m.unit_cost)}</td>
                            <td className="px-3 py-2 text-muted-foreground truncate max-w-[120px]">{m.supplier || '—'}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{m.lead_time_days}gg</td>
                            {isAdmin && (
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => setMatForm({ open: true, initial: m })}
                                    className="p-1 rounded hover:bg-muted" title="Modifica">
                                    <Pencil size={11} className="text-muted-foreground" />
                                  </button>
                                  <button onClick={() => deleteMaterial(m.id)}
                                    disabled={deleting === m.id}
                                    className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950" title="Elimina">
                                    {deleting === m.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} className="text-red-500" />}
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ── Movements ── */}
        <TabsContent value="movements" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40 text-muted-foreground">
                      <th className="text-left px-4 py-2">Data</th>
                      <th className="text-left px-4 py-2">Materiale</th>
                      <th className="text-left px-4 py-2">Tipo</th>
                      <th className="text-right px-4 py-2">Quantità</th>
                      <th className="text-right px-4 py-2">Costo unit.</th>
                      <th className="text-right px-4 py-2">Costo totale</th>
                      <th className="text-left px-4 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Nessun movimento registrato</td></tr>
                    ) : movements.map(m => {
                      const mt = MOVEMENT_TYPES.find(t => t.value === m.movement_type);
                      return (
                        <tr key={m.id} className="border-b hover:bg-muted/20">
                          <td className="px-4 py-2 font-mono">{m.movement_date}</td>
                          <td className="px-4 py-2 font-medium">{m.material_name}</td>
                          <td className={`px-4 py-2 font-medium ${mt?.color || ''}`}>
                            {m.quantity >= 0 ? <ArrowUpCircle size={10} className="inline mr-1" /> : <ArrowDownCircle size={10} className="inline mr-1" />}
                            {mt?.label || m.movement_type}
                          </td>
                          <td className={`px-4 py-2 text-right font-semibold ${m.quantity >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                            {m.quantity >= 0 ? '+' : ''}{fmt(m.quantity, 4)}
                          </td>
                          <td className="px-4 py-2 text-right text-muted-foreground">{fmtEur(m.unit_cost)}</td>
                          <td className="px-4 py-2 text-right">{fmtEur(m.total_cost)}</td>
                          <td className="px-4 py-2 text-muted-foreground truncate max-w-[150px]">{m.notes || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <MaterialForm
        open={matForm.open}
        onClose={() => setMatForm({ open: false, initial: null })}
        onSave={saveMaterial}
        initial={matForm.initial}
      />
      <MovementForm
        open={movForm}
        onClose={() => setMovForm(false)}
        onSave={saveMovement}
        materials={materials}
      />
    </div>
  );
}
